import React, { useState } from "react"
import { DestinationStatuses, Location, PODObject, TableOrder } from "@/types/orders.types"
import { isPayingUserSelector, isSupportRoleSelector } from "@/atoms/userAtom"
import { faChevronDown, faChevronRight, faFlag } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Divider, Typography, message, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import {
  getArrivalTime,
  getDropoffByTimesNonStrict,
  getPickupTimeOrEta,
  getScheduleTime,
  getStopOrOrderDropoffTime,
} from "@/utils/order-time-calculations"
import { formatOrderChildren } from "@/atoms/ordersAtom"
import { DateTime } from "luxon"
import DropoffDetails from "@/modules/active-orders/OrderTable/components/DropoffDetails/DropoffDetails"
import DropoffStopsCount from "@/modules/active-orders/OrderTable/components/DropoffDetails/DropoffStopsCount"
import PickupDetails from "@/modules/active-orders/OrderTable/components/PickupDetails/PickupDetails"
import PickupLocationAlias from "@/modules/active-orders/OrderTable/components/PickupDetails/PickupLocationAlias"
import ActiveStatusCell from "@/modules/active-orders/OrderTable/components/cells/ActiveStatusCell"
import { EditDropoffTimePicker } from "@/modules/shared/EditDropoffTimePicker/EditDropoffTimePicker"
import DisplayPriceBreakdown from "@/modules/shared/DisplayPriceBreakdown/DisplayPriceBreakdown"
import DisplayOrderTrackingURL from "@/modules/active-orders/ActiveOrdersFiltersAndActions/components/DisplayOrderTrackingURL"
import { ContactDriverButton } from "@/UI/Buttons/ContactDriverButton"
import { OrderStatusesTypes } from "@/types/statuses.types"
import { isOrderStatusFinal, isStopStatusFinal } from "@/utils/status"
import { dispatchPageModalsAtom } from "@/atoms/dispatchPageModalsAtom"
import { Stop } from "@/types/stops.types"
import UpdateStopStatusSelect from "@/modules/active-orders/OrderTable/components/StatusSelect/UpdateStopStatusSelect"
import { stopsAtom } from "@/atoms/stopsAtom"
import { isEmpty } from "lodash"
import SquareIconContainer from "@/UI/Icons/SquareIconContainer"
import { updateDeliveryArrivalStatus } from "@/api/deliveries"
import { getFailureReasonText } from "@/utils/failureReason"

interface OrderDetailsSummaryProps {
  order: TableOrder
  pickupPODs: PODObject[] | undefined
  dropoffPODs: Record<string, PODObject[]> | undefined
  selectedStop?: Stop
  updateSingleOrderStopStatus: (
    orderId: string,
    newStatus: DestinationStatuses,
    index: number
  ) => Promise<void>
  isLoading: boolean
  updateOrderStatus: (orderId: string, newStatus: OrderStatusesTypes) => Promise<void>
  triggerGetDelivery?: () => Promise<void>
}

const { Text } = Typography

const OrderDetailsSummary: React.FC<OrderDetailsSummaryProps> = ({
  order,
  pickupPODs,
  dropoffPODs,
  isLoading,
  selectedStop,
  updateSingleOrderStopStatus,
  updateOrderStatus,
  triggerGetDelivery,
}) => {
  const { t } = useTranslation()
  const setDispatchPageModals = useSetRecoilState(dispatchPageModalsAtom)
  const [{ stops: stopsState }, setStops] = useRecoilState(stopsAtom)
  const [isDropoffExpanded, setIsDropoffExpanded] = useState(true)
  const [isArrivalStatusLoading, setIsArrivalStatusLoading] = useState(false)

  const isDeliveryDone = isOrderStatusFinal(
    order?.isChildren ? order?.parentStatus! : order?.status!
  )

  const isSupportRole = useRecoilValue(isSupportRoleSelector)
  const isPayingUser = useRecoilValue(isPayingUserSelector)

  const recipients = !!selectedStop ? ([selectedStop?.recipient] as Location[]) : order?.recipients

  const stops = recipients?.length > 1 ? formatOrderChildren(order) : recipients
  const allStopsCompleted = stops
    ?.map((s) => s.status)
    ?.every((status) => isStopStatusFinal(status))

  const handleStopStatusUpdate = async (
    orderId: string,
    newStatus: DestinationStatuses,
    index: number
  ) => {
    try {
      await updateSingleOrderStopStatus(orderId, newStatus, index)
      setStops((stopState) => {
        return {
          ...stopState,
          stops: stopState?.stops?.map((stop) => {
            if (stop.id === orderId && stop.stopIndex === index) {
              return { ...stop, recipient: { ...stop.recipient, status: newStatus } }
            }
            return stop
          }),
        }
      })
    } catch {
      message.error(t("errors.common"))
    }
  }

  const endContact =
    order.arrival?.location.alias || order.arrival?.contact.name || t("common.endLocation")
  const endAddress = order.arrival?.location.address ?? order?.metadata?.endAddress
  const arrivalTime = order.cancelledAt ? "" : getArrivalTime(order, t)
  const handleArrivalStatusUpdate = async () => {
    setIsArrivalStatusLoading(true)
    try {
      await updateDeliveryArrivalStatus(order.id)
      await triggerGetDelivery?.()
    } catch (error) {
      message.error(t("errors.common"))
    } finally {
      setIsArrivalStatusLoading(false)
    }
  }

  const sections = [
    {
      title: (
        <div className="flex justify-between">
          <p className="!mb-0">{t("active-orders.OrderDetailsModal.driverInformation")}</p>
          <DisplayOrderTrackingURL
            provider={order.provider}
            status={order.status}
            trackingUrl={order.trackingUrl}
            recipients={recipients}
          />
        </div>
      ),
      content: (
        <div className="flex">
          <div className="flex flex-col w-9/12">
            {order?.driver?.name ? (
              <>
                <p className="text-gray-500">{order?.driver?.name}</p>
                <p className="text-gray-500">{order?.driver?.plateNumber}</p>
                <div className="flex gap-x-2">
                  <p className="text-gray-500">{order?.driver?.phone}</p>
                  {!!order?.driver?.phone && <ContactDriverButton phone={order?.driver?.phone} />}
                </div>
                {/* {order?.driver?.photoUrl && <Image src={order?.driver?.photoUrl} width={48} />} */}
              </>
            ) : (
              <p className="text-kosmoGray">
                {t("active-orders.OrderInformation.noRiderInformation")}
              </p>
            )}
          </div>
        </div>
      ),
    },
    // Route information
    {
      title: t("common.route"),
      content: (
        <div className="flex gap-x-1">
          <p className="font-semibold">{order.orderID}</p>
          <p>-</p>
          <p
            onClick={() =>
              setDispatchPageModals((dispatchPageModals) => ({
                ...dispatchPageModals,
                orderDetailsModal: {
                  ...dispatchPageModals.orderDetailsModal,
                  stopIndex: undefined,
                },
              }))
            }
            className="italic hover:underline cursor-pointer"
          >
            {t("active-orders.OrderDetailsModal.seeRoute")}
          </p>
        </div>
      ),
      hidden: !selectedStop?.id,
    },
    {
      title: <PickupLocationAlias pickup={order?.sender} />,
      content: (
        <div className="flex">
          <div className="w-8/12">
            <PickupDetails
              pickup={order?.sender}
              isDeliveryDone={isDeliveryDone}
              withIcon={false}
              withTime={true}
              withPhone={true}
              withAddress={true}
              withPods={true}
              withNotes={true}
              withAlias={false}
              pods={pickupPODs}
              isPickedUp={!!order.pickupAt}
            />
          </div>
          <div className="w-4/12 flex flex-col text-end">
            <span className="font-normal text-gray-500 mb-0">
              {getScheduleTime(
                order?.schedule?.pickup_at,
                {
                  withHumanDate: true,
                  withPrefix: true,
                },
                t
              )}
            </span>
            <span className="font-normal text-gray-500 mb-0">
              {getPickupTimeOrEta(order, t, true)}
            </span>
            {!!order?.sender?.noteFromDriver && (
              <span className="font-normal text-gray-500 lowercase">
                {`${t("common.with")} `}
                <Tooltip title={order.sender?.noteFromDriver}>
                  <span className="underline cursor-pointer">{t("common.driversNote")}</span>
                </Tooltip>
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: <></>,
      content: (
        <div className="flex flex-col space-y-4">
          <div>
            {recipients?.length > 1 && (
              <div className="flex">
                <div className="w-8/12">
                  <div
                    className="flex space-x-2 w-fit items-center cursor-pointer"
                    onClick={() => setIsDropoffExpanded(!isDropoffExpanded)}
                  >
                    <DropoffStopsCount order={order} />{" "}
                    <FontAwesomeIcon
                      size="xs"
                      className="flex-shrink-0"
                      icon={isDropoffExpanded ? faChevronDown : faChevronRight}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          {isDropoffExpanded && (
            <div className="flex flex-col space-y-4">
              {stops?.map((stop, currentStopIndex) => {
                const isChildren = "isChildren" in stop
                const recipient = isChildren ? stop?.recipient : stop
                const stopPods = stop?.trackingUrl ? dropoffPODs?.[stop.trackingUrl] : undefined
                const promisedDeliveryTimeRange = getDropoffByTimesNonStrict(
                  DateTime.fromMillis(stop?.expectedArrival?.from || 0),
                  DateTime.fromMillis(stop?.expectedArrival?.to || 0),
                  t
                )
                return (
                  <div key={currentStopIndex} className="flex gap-x-2">
                    {stops.length > 1 && (
                      <div
                        className={`flex w-5 h-5 items-center justify-center rounded-md relative bg-gray-100`}
                        style={{ width: "20px", height: "20px" }}
                      >
                        <p className="text-grey-900 font-semibold text-sm">
                          {currentStopIndex + 1}
                        </p>
                      </div>
                    )}
                    <div className="w-9/12">
                      <DropoffDetails
                        order={order}
                        withDetails={true}
                        ellipsis={false}
                        dropoff={recipient}
                        shortAddress={false}
                        pods={stopPods}
                        withTrackingUrl={true}
                        uploadPodCallback={triggerGetDelivery}
                        stopIndex={currentStopIndex}
                        // TODO: Is empty is temporarily fixing empty state for navigation
                        {...(!!selectedStop || isEmpty(stopsState)
                          ? {}
                          : {
                              onNameClick: () =>
                                setDispatchPageModals((dispatchPageModals) => ({
                                  ...dispatchPageModals,
                                  orderDetailsModal: {
                                    ...dispatchPageModals.orderDetailsModal,
                                    stopIndex: currentStopIndex,
                                  },
                                })),
                            })}
                      />
                    </div>
                    <div className="flex flex-col w-4/12 text-end">
                      {/* Fallback on order level ETA when single stop to match table */}
                      {!!promisedDeliveryTimeRange && (
                        <p className="flex-shrink-0 text-gray-500">{promisedDeliveryTimeRange}</p>
                      )}
                      <div className="flex justify-end items-center gap-1 group">
                        <p className="flex-shrink-0 text-gray-500">
                          {getStopOrOrderDropoffTime(order, true, recipient, t)}
                        </p>
                        <span className="invisible group-hover:visible">
                          <EditDropoffTimePicker
                            orderId={order.id}
                            visible={recipient?.status === DestinationStatuses.DELIVERED}
                            recipients={recipients}
                            dropoffIndex={currentStopIndex}
                          />
                        </span>
                      </div>
                      {selectedStop ? (
                        <UpdateStopStatusSelect
                          orderId={selectedStop.id}
                          routeStatus={selectedStop.status}
                          currentStatus={
                            selectedStop?.recipient?.status || DestinationStatuses.CREATED
                          }
                          stopIndex={selectedStop.stopIndex}
                          availableStatuses={selectedStop?.availableStatuses?.stops?.[0]}
                          updateStopStatus={handleStopStatusUpdate}
                        />
                      ) : (
                        <ActiveStatusCell
                          order={isChildren ? (stop as any) : order}
                          isUpdatable={!isSupportRole && isPayingUser}
                          justfiyContent="justify-end"
                          isLoading={isLoading}
                          updateStopStatus={updateSingleOrderStopStatus}
                          updateOrderStatus={updateOrderStatus}
                        />
                      )}
                      {!!recipient?.noteFromDriver && (
                        <span className="font-normal text-gray-500 lowercase">
                          {`${t("common.with")} `}
                          <Tooltip title={recipient.noteFromDriver}>
                            <span className="underline cursor-pointer">
                              {t("common.driversNote")}
                            </span>
                          </Tooltip>
                        </span>
                      )}
                      {!!recipient?.failureReason && (
                        <span className="font-normal text-gray-500 lowercase">
                          <div style={{ whiteSpace: "pre-line" }}>
                            {`${t("common.with")} `}
                            <Tooltip
                              title={getFailureReasonText(
                                t,
                                recipient.failureReason,
                                recipient?.failureReasonNote
                              )}
                            >
                              <span className="underline cursor-pointer">
                                {t("common.failureReason")}
                              </span>
                            </Tooltip>
                          </div>
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: <></>,
      content: (
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="flex space-x-2">
              <SquareIconContainer
                color="bg-kosmoLightGray"
                icon={
                  <FontAwesomeIcon
                    className="flex-shrink-0 h-3 mx-auto my-auto text-black text-sm"
                    icon={faFlag}
                  />
                }
              />
              <span className="text-black font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                {endContact}
              </span>
            </div>
            <span className="font-normal text-gray-500 mb-0">{endAddress}</span>
          </div>
          {!!arrivalTime && (
            <div className="flex flex-col items-end">
              <span className="font-normal text-gray-500 mb-0 mr-2">{arrivalTime}</span>
              {allStopsCompleted && (
                <UpdateStopStatusSelect
                  isLoading={isLoading || isArrivalStatusLoading}
                  orderId={`${order?.id}-arrival`}
                  stopIndex={-1}
                  currentStatus={
                    order.cancelledAt
                      ? DestinationStatuses.CANCELED
                      : order?.arrival?.finishedAt
                      ? DestinationStatuses.DELIVERED
                      : DestinationStatuses.TO_DROPOFF
                  }
                  availableStatuses={
                    !order?.arrival?.finishedAt ? [DestinationStatuses.DELIVERED] : []
                  }
                  updateStopStatus={handleArrivalStatusUpdate}
                />
              )}
            </div>
          )}
        </div>
      ),
      hidden: !endAddress || selectedStop?.id,
    },
    {
      title: <></>,
      content: <DisplayPriceBreakdown priceBreakdown={order?.priceBreakdown} />,
      hidden: selectedStop,
    },
  ]

  return (
    <div className="flex flex-col h-full">
      {sections.map((section, index) => {
        if (section.hidden) {
          return <></>
        }
        return (
          <React.Fragment key={index}>
            <Text strong>{section?.title}</Text>
            {section.content}
            <Divider style={{ marginBottom: "15px", marginTop: "15px" }} />
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default OrderDetailsSummary
